import { useEffect, useMemo, useState } from 'react';
import {
  normalizeFieldDef,
  type AiOutputFormat,
  contrastTextColor,
  type AnalysisAxisRule,
  type AnalysisDisplayRules,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';
import { usePhotoUrl, type AiCategoryMastersResult } from '../posts';
import { expandAllToClassificationRows } from './classificationAdapter';
import {
  buildAxisGroups,
  buildAxisOptions,
  clsRowToPost,
  collectCustomAxes,
  collectIncludeEmptyKeys,
  deriveAxisOptionsFromDataSettings,
  isDefaultAxisKey,
  mirrorRowToExtraFields,
  postCodesForAxis,
  type AxisGroup,
  type AxisKey,
  type PostFieldAxisGroup,
} from './matrixAxisModel';
import type { AnalysisPerspective } from './analysisPerspectives';

/**
 * 分析アプリ「分析(詳細選択)」タブ。
 *
 * マトリクス/地図の粗選択を受けて、選んだ投稿群を **マトリクスと同一の軸エンジン**
 * (`matrixAxisModel`) で再構成する画面。要件:
 *   1. データソース (投稿単位 / AI 認識物単位) をこの画面でも切替できる。マトリクスから
 *      遷移したときは元のデータソースを引き継ぐ (initialContext.dataSource)。
 *   2. マトリクスから来たときの既定表示はマトリクスと同じ (同じ縦横軸 + 同じデータソースの
 *      2 軸グルーピンググリッド)。
 *   3. 選んだデータに対し、縦軸・横軸を任意の軸へ変更でき (固定しない)、さらに任意の軸で
 *      バケット絞込 (フィルタ) もかけられる。軸・フィルタはすべて master / dataSettings /
 *      M5 ルール由来で動的導出 (ハードコードしない)。
 *   4. AI 解析結果ステータス (すべて/解析済/未解析/解析失敗) の絞込は撤去。
 *
 * v2 の有用な独自機能は残置: keyword 検索 / 並び替え / card↔table 切替 / 複数選択 →
 * 「気づきの記入へ」導線。
 */

/** マトリクス/地図 → 分析(詳細選択) の粗選択 context。 */
export interface AnalysisDetailContext {
  source: 'matrix' | 'map';
  /** マトリクス由来時の縦軸キー (matrixAxisModel の AxisKey: 'emotion.major' / 'postField:HappinessScore' 等)。 */
  vAxisKey?: string;
  /** マトリクス由来時の横軸キー。 */
  hAxisKey?: string;
  /** 粗選択元のデータソース (投稿単位 / AI 認識物単位)。マトリクス由来のみ指定。 */
  dataSource?: 'post' | 'cls';
}

/** 気づきワークスペースへ渡す絞込 context。 */
export interface ProceedContext {
  emotion?: string;
  categoryLabel?: string;
  filterList?: string[];
}

export interface AnalysisListViewProps {
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
  posts: PostMinimal[];
  resolver: AiCategoryMastersResult;
  onSelectPost: (post: PostMinimal) => void;
  /**
   * 指定すると multi-select 列が有効化、選択中 post 群を「💡 気づき作成へ」 button で
   * 親の `onProceedToInsight` に転送する。未指定なら従来通り単 click のみ。
   */
  selectedPostIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** 「💡 気づきの記入へ」 button が押されたとき。AnalysisPage が front ワークスペースへ handoff。 */
  onProceedToInsight?: (postIds: string[], context?: ProceedContext) => void;
  /** dataSettings 由来の postFields (軸 / フィルタ候補の動的導出に使う)。 */
  postFields?: FieldDef[];
  /** マトリクス/地図からの粗選択 context。既定の縦横軸 / データソースを決める。 */
  initialContext?: AnalysisDetailContext | null;
  /** M3 出力フォーマット。AI 認識物 (cls) 展開 + 軸 code 解決に使う (マトリクスと同じ)。 */
  outputFormat?: AiOutputFormat | null;
  /** 地区モード (cls 展開の fallback。通常は outputFormat.mode を優先)。 */
  mode?: DistrictMode | null;
  /** M5 マトリクス表示ルール (既定軸 / custom 軸 / hiddenAxisKeys / includeEmpty)。 */
  matrixRules?: AnalysisDisplayRules['matrix'];
}

type SortKey = 'createdAt-desc' | 'createdAt-asc' | 'agreeCount-desc' | 'analyzedAt-desc';
type ViewMode = 'card' | 'table';

const SORT_LABEL: Record<SortKey, string> = {
  'createdAt-desc': '投稿: 新しい順',
  'createdAt-asc': '投稿: 古い順',
  'agreeCount-desc': '同感数: 多い順',
  'analyzedAt-desc': '解析: 新しい順',
};

function comparePosts(a: PostMinimal, b: PostMinimal, key: SortKey): number {
  switch (key) {
    case 'createdAt-desc':
      return (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0);
    case 'createdAt-asc':
      return (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0);
    case 'agreeCount-desc':
      return b.agreeCount - a.agreeCount;
    case 'analyzedAt-desc': {
      const av = a.aiAnalysisAnalyzedAt ? new Date(a.aiAnalysisAnalyzedAt).getTime() : 0;
      const bv = b.aiAnalysisAnalyzedAt ? new Date(b.aiAnalysisAnalyzedAt).getTime() : 0;
      return bv - av;
    }
    default:
      return 0;
  }
}

/** initialContext / M5 既定 / axisOptions 順 の優先で既定軸 key を解決。 */
function resolveDefaultAxisKey(
  contextKey: string | undefined,
  ruleKey: string | undefined,
  axisOptions: { key: string }[],
  fallbackIdx: number,
): string {
  const inOptions = (k: string | undefined): k is string =>
    !!k && axisOptions.some((o) => o.key === k);
  if (inOptions(contextKey)) return contextKey;
  if (inOptions(ruleKey)) return ruleKey;
  return axisOptions[fallbackIdx]?.key ?? axisOptions[0]?.key ?? '';
}

export function AnalysisListView({
  perspectives,
  defaultPerspectiveLabel,
  posts,
  resolver,
  onSelectPost,
  selectedPostIds,
  onSelectionChange,
  onProceedToInsight,
  postFields,
  initialContext,
  outputFormat,
  mode,
  matrixRules,
}: AnalysisListViewProps) {
  /* ---------- multi-select ---------- */
  const multiSelectEnabled = Boolean(onSelectionChange);
  const selectedSet = useMemo(() => new Set(selectedPostIds ?? []), [selectedPostIds]);
  const togglePostSelection = (postId: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedPostIds ?? []);
    if (next.has(postId)) next.delete(postId);
    else next.add(postId);
    onSelectionChange([...next]);
  };
  const setSelectionTo = (ids: string[]) => onSelectionChange?.(ids);

  /* ---------- 軸エンジン infra (マトリクスと共有) ---------- */
  // normalizeFieldDef で legacy/CSV import の options を {value,label}[] に修復してから軸化。
  const normFields = useMemo(() => (postFields ?? []).map(normalizeFieldDef), [postFields]);
  const customAxes = useMemo(() => collectCustomAxes(matrixRules), [matrixRules]);
  const customRuleByKey = useMemo(() => {
    const m = new Map<string, AnalysisAxisRule>();
    for (const c of customAxes) m.set(c.key, c.rule);
    return m;
  }, [customAxes]);
  const postFieldAxes = useMemo(() => deriveAxisOptionsFromDataSettings(normFields), [normFields]);
  const postFieldAxisByKey = useMemo(() => {
    const m = new Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>();
    for (const a of postFieldAxes) m.set(a.key, { field: a.field, groups: a.groups });
    return m;
  }, [postFieldAxes]);
  const includeEmptyKeys = useMemo(() => collectIncludeEmptyKeys(matrixRules), [matrixRules]);
  const axisOptions = useMemo(
    () =>
      buildAxisOptions({
        resolver,
        postFieldAxes: postFieldAxes.map((a) => ({ key: a.key, label: a.label })),
        customAxes: customAxes.map((c) => ({ key: c.key, label: c.label })),
        hiddenAxisKeys: matrixRules?.hiddenAxisKeys,
        perspectives,
        defaultPerspectiveLabel,
      }),
    [
      resolver,
      postFieldAxes,
      customAxes,
      matrixRules?.hiddenAxisKeys,
      perspectives,
      defaultPerspectiveLabel,
    ],
  );
  const axisLabelOf = (k: AxisKey) => axisOptions.find((o) => o.key === k)?.label ?? k;

  /* ---------- データソース (投稿単位 / AI 認識物単位) ---------- */
  // マトリクスと同じく outputFormat.mode で classifications を展開する。
  const classificationRows = useMemo(
    () =>
      expandAllToClassificationRows(posts, outputFormat, outputFormat?.mode ?? mode ?? undefined),
    [posts, outputFormat, mode],
  );
  const hasClassifications = classificationRows.length > 0;
  const clsNegativeLabel = outputFormat?.schema?.assessment?.negativeLabel;
  // AI 認識物単位の各行に親投稿の入力項目 (KeikanType 等) を継承させるための postId→extraFields。
  const postExtraById = useMemo(() => {
    const m = new Map<string, Record<string, unknown>>();
    for (const p of posts) if (p.extraFields) m.set(p.id, p.extraFields);
    return m;
  }, [posts]);
  const clsAsPosts = useMemo<PostMinimal[]>(
    () =>
      classificationRows.map((row) =>
        clsRowToPost(row, clsNegativeLabel, postExtraById.get(row.postId) ?? null),
      ),
    [classificationRows, clsNegativeLabel, postExtraById],
  );
  // 投稿単位は「代表 = rank 最小の分類」の値を extraFields にミラー (マトリクス projectedPosts と同じ)。
  const repRowByPost = useMemo(() => {
    const m = new Map<string, (typeof classificationRows)[number]>();
    for (const row of classificationRows) {
      const cur = m.get(row.postId);
      if (!cur || Number(row.rank) < Number(cur.rank)) m.set(row.postId, row);
    }
    return m;
  }, [classificationRows]);
  const projectedPosts = useMemo<PostMinimal[]>(() => {
    if (repRowByPost.size === 0) return posts;
    return posts.map((p) => {
      const rep = repRowByPost.get(p.id);
      if (!rep) return p;
      return {
        ...p,
        extraFields: { ...(p.extraFields ?? {}), ...mirrorRowToExtraFields(rep, clsNegativeLabel) },
      };
    });
  }, [posts, repRowByPost, clsNegativeLabel]);

  const [dataSource, setDataSource] = useState<'post' | 'cls'>(
    initialContext?.dataSource === 'cls' ? 'cls' : 'post',
  );
  // initialContext が変わる (新しい粗選択で遷移) たびにデータソースを引き継ぐ。
  useEffect(() => {
    if (initialContext?.dataSource) setDataSource(initialContext.dataSource);
  }, [initialContext]);
  // classifications が消えたら post に戻す (cls 選択中に投稿/format が変わるケース)。
  useEffect(() => {
    if (!hasClassifications && dataSource === 'cls') setDataSource('post');
  }, [hasClassifications, dataSource]);
  const isClsMode = dataSource === 'cls' && hasClassifications;
  const sourcePosts = isClsMode ? clsAsPosts : projectedPosts;

  const axisDeps = useMemo(
    () => ({
      resolver,
      outputFormat,
      postFieldAxisByKey,
      customRuleByKey,
      includeEmptyKeys,
      perspectives,
    }),
    [resolver, outputFormat, postFieldAxisByKey, customRuleByKey, includeEmptyKeys, perspectives],
  );

  /* ---------- 基本フィルタ (v2 独自、有用なため残置) ---------- */
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt-desc');

  /* ---------- 動的フィルタ (軸駆動): axisKey → 選択 bucket code 集合 ---------- */
  const [filterSelections, setFilterSelections] = useState<Record<string, Set<string>>>({});
  // 「▶分析設定」折りたたみ: 検索・表示形式・並替・データソース・軸・写真サイズ・フィルタ を
  // すべてここに集約 (全画面幅で既定は閉。選択ツールと CTA は折りたたみ外で常時表示)。
  // フィルタは独立した折りたたみを持たず、分析設定を開くと bucket chip が直接展開される。
  const [analysisSettingsOpen, setAnalysisSettingsOpen] = useState(false);

  // フィルタチップ用の bucket は sourcePosts (絞込前) から導出 (絞込で選択肢が消えないように)。
  const filterGroupsByKey = useMemo(() => {
    const m = new Map<string, AxisGroup[]>();
    for (const opt of axisOptions) {
      m.set(
        opt.key,
        buildAxisGroups(opt.key, {
          perspectives,
          resolver,
          posts: sourcePosts,
          isVertical: false,
          postFieldAxisByKey,
          customRuleByKey,
          includeEmptyKeys,
        }),
      );
    }
    return m;
  }, [
    axisOptions,
    resolver,
    sourcePosts,
    postFieldAxisByKey,
    customRuleByKey,
    includeEmptyKeys,
    perspectives,
  ]);

  /* ---------- 表示モード ---------- */
  // マトリクス/地図から粗選択して来た (initialContext あり) ときは card grid を初手で見せ、
  // 直接「分析」タブを開いた時は table を既定にする。
  // 既定はカード表示 (2026-09-02 指示)。写真で全体を掴んでから必要に応じて表へ切り替える運用。
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [cardSize, setCardSize] = useState(72);

  /* ---------- フィルタ適用 (AND 合成) ---------- */
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const activeAxisKeys = Object.keys(filterSelections).filter(
      (k) => (filterSelections[k]?.size ?? 0) > 0,
    );
    const result = sourcePosts.filter((p) => {
      if (kw) {
        const hay = `${p.title} ${p.comment ?? ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      for (const axisKey of activeAxisKeys) {
        const sel = filterSelections[axisKey]!;
        const groups = filterGroupsByKey.get(axisKey) ?? [];
        const codes = postCodesForAxis(p, axisKey, groups, axisDeps);
        let ok = false;
        for (const c of codes) {
          if (sel.has(c)) {
            ok = true;
            break;
          }
        }
        if (!ok) return false;
      }
      return true;
    });
    return result.sort((a, b) => comparePosts(a, b, sortKey));
  }, [sourcePosts, keyword, filterSelections, filterGroupsByKey, sortKey, axisDeps]);

  /* ---------- card grid 軸 (変更可能・固定しない) ---------- */
  const defaultVAxis = useMemo(
    () =>
      resolveDefaultAxisKey(initialContext?.vAxisKey, matrixRules?.defaultVAxis, axisOptions, 0),
    [initialContext, matrixRules?.defaultVAxis, axisOptions],
  );
  const defaultHAxis = useMemo(
    () =>
      resolveDefaultAxisKey(initialContext?.hAxisKey, matrixRules?.defaultHAxis, axisOptions, 1),
    [initialContext, matrixRules?.defaultHAxis, axisOptions],
  );
  const [vAxisKey, setVAxisKey] = useState('');
  const [hAxisKey, setHAxisKey] = useState('');
  // initialContext / axisOptions が確定/変化したら既定軸へ同期 (ユーザ変更は selector で上書き)。
  useEffect(() => {
    setVAxisKey(defaultVAxis);
    setHAxisKey(defaultHAxis);
  }, [defaultVAxis, defaultHAxis]);
  const effVAxis = vAxisKey || defaultVAxis;
  const effHAxis = hAxisKey || defaultHAxis;

  // grid 軸の bucket (絞込後 filtered から導出 = マトリクスと同じ)。
  const vGroups = useMemo(
    () =>
      buildAxisGroups(effVAxis, {
        perspectives,
        resolver,
        posts: filtered,
        isVertical: true,
        postFieldAxisByKey,
        customRuleByKey,
        includeEmptyKeys,
      }),
    [
      effVAxis,
      resolver,
      filtered,
      postFieldAxisByKey,
      customRuleByKey,
      includeEmptyKeys,
      perspectives,
    ],
  );
  const hGroups = useMemo(
    () =>
      buildAxisGroups(effHAxis, {
        perspectives,
        resolver,
        posts: filtered,
        isVertical: false,
        postFieldAxisByKey,
        customRuleByKey,
        includeEmptyKeys,
      }),
    [
      effHAxis,
      resolver,
      filtered,
      postFieldAxisByKey,
      customRuleByKey,
      includeEmptyKeys,
      perspectives,
    ],
  );

  const gridCells = useMemo(() => {
    const cells = new Map<string, PostMinimal[]>();
    const vIsDefault = isDefaultAxisKey(effVAxis);
    const hIsDefault = isDefaultAxisKey(effHAxis);
    for (const p of filtered) {
      // 既定 (master) 軸は AI 解析完了 post のみ集計 (マトリクスと同じ)。
      if ((vIsDefault || hIsDefault) && p.aiAnalysisStatus !== 'done') continue;
      // 投稿単位はマトリクスと同じく「1 投稿 = 1 マス」。複数値を持つ軸は先頭 (最も当てはまる値)
      // だけを対象にし、同じ投稿が複数セルに二重計上されるのを防ぐ。
      const vCodes = firstCodeOnly(postCodesForAxis(p, effVAxis, vGroups, axisDeps), isClsMode);
      const hCodes = firstCodeOnly(postCodesForAxis(p, effHAxis, hGroups, axisDeps), isClsMode);
      for (const v of vCodes) {
        for (const h of hCodes) {
          const key = `${v}:${h}`;
          const arr = cells.get(key);
          if (arr) arr.push(p);
          else cells.set(key, [p]);
        }
      }
    }
    return cells;
  }, [filtered, effVAxis, effHAxis, vGroups, hGroups, axisDeps, isClsMode]);

  /* ---------- 気づき作成 handoff ---------- */
  const buildFilterList = (): string[] => {
    const list: string[] = [];
    for (const opt of axisOptions) {
      const sel = filterSelections[opt.key];
      if (!sel || sel.size === 0) continue;
      const groups = filterGroupsByKey.get(opt.key) ?? [];
      const labels = [...sel].map((code) => groups.find((g) => g.code === code)?.label ?? code);
      list.push(`${opt.label}: ${labels.join(', ')}`);
    }
    return list;
  };

  const handleProceed = () => {
    if (!onProceedToInsight) return;
    const picked = filtered.filter((p) => selectedSet.has(p.id));
    const target = picked.length > 0 ? picked : filtered;
    // cls モードでは id が `${postId}__cls${rank}` なので base postId に正規化 + dedup。
    const ids = [...new Set(target.map((p) => p.id.split('__cls')[0] ?? p.id))];
    onProceedToInsight(ids, { filterList: buildFilterList() });
  };

  /* ---------- フィルタ操作 ---------- */
  const toggleFilterValue = (axisKey: string, code: string) => {
    setFilterSelections((prev) => {
      const next = { ...prev };
      const set = new Set(next[axisKey] ?? []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      next[axisKey] = set;
      return next;
    });
  };
  const activeFilterCount = Object.values(filterSelections).filter(
    (s) => (s?.size ?? 0) > 0,
  ).length;
  const clearAllFilters = () => setFilterSelections({});

  const toggleAllVisible = () => {
    const ids = filtered.map((p) => p.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedSet.has(id));
    const next = new Set(selectedPostIds ?? []);
    if (allSelected) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    setSelectionTo([...next]);
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedSet.has(p.id));

  // データソース切替時は選択をクリア (post id と cls id が混ざらないように)。
  const switchDataSource = (next: 'post' | 'cls') => {
    if (next === dataSource) return;
    setDataSource(next);
    setSelectionTo([]);
  };

  // 件数表示の母数 (post: 投稿数 / cls: AI 認識物数)。
  const unitLabel = isClsMode ? 'AI認識物' : '件';

  // 「気づきの記入へ」CTA は上部 (右寄せ) と最下部の 2 箇所に出す。最下部だけだと、一覧が長い
  // ときにスクロールしないと見えず「気づけない」ため (マトリクス/地図は上部右に分析 CTA がある)。
  // どちらも同じ handleProceed / 同じ件数を使い、文言と挙動を一致させる。
  const showProceedCta = Boolean(onProceedToInsight) && filtered.length > 0;
  const proceedCount = selectedSet.size > 0 ? selectedSet.size : filtered.length;

  /* ====================================================================== */
  return (
    <div className="flex flex-col h-full">
      {/* ===== 制御バー =====
          「▶分析設定」折りたたみに 検索/表示形式/並替/データソース/軸/写真サイズ/フィルタ を集約。
          選択ツール (✓キープ / すべて選択 / 選択中) は分析設定の「上」に常時表示。 */}
      <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-slate-200 bg-white flex flex-col gap-2 shrink-0">
        {/* 選択ツール (✓キープ / すべて選択 / 選択中) + 気づき CTA — 分析設定より上に配置。
            CTA は `ml-auto` で右寄せ。折り返しても行の右端に留まるので、スマホの狭い幅でも
            「右上に出る」見え方が保たれる (justify-between だと折返し時に左端に付く)。 */}
        {(multiSelectEnabled || showProceedCta) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {multiSelectEnabled && (
              <>
                <span className="text-xs text-slate-500">
                  ✓でキープして「気づきの記入へ」に進みます
                </span>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="すべて選択"
                  />
                  すべて選択
                </label>
                <span className="text-xs text-slate-600">
                  選択中: <strong>{selectedSet.size}</strong> 件
                </span>
                {selectedSet.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectionTo([])}
                    className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                  >
                    解除
                  </button>
                )}
              </>
            )}
            {showProceedCta && (
              <button
                type="button"
                onClick={handleProceed}
                /* 見た目はマトリクスの「📊 分析」CTA と揃える (accent + shadow/ring)。
                   最下部の CTA と同一動作なので、どちらを押しても結果は同じ。 */
                className="ml-auto shrink-0 min-h-touch text-xs px-3 py-1.5 rounded-md font-bold text-white shadow-md ring-2 ring-accent-600/40 bg-accent-600 hover:bg-accent-700 active:bg-accent-800"
              >
                💡 気づきの記入へ ({proceedCount}件)
              </button>
            )}
          </div>
        )}

        {/* 分析設定トグル + 件数 */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <button
            type="button"
            onClick={() => setAnalysisSettingsOpen((v) => !v)}
            aria-expanded={analysisSettingsOpen}
            className={`shrink-0 text-xs px-2.5 py-1 rounded font-semibold border min-h-touch ${
              analysisSettingsOpen || activeFilterCount > 0
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {analysisSettingsOpen ? '▼' : '▶'} 分析設定
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">
            {filtered.length}/{sourcePosts.length} {unitLabel}
          </span>
        </div>

        {/* ===== 分析設定 折りたたみ (既定で閉、▶で開閉) ===== */}
        <div
          className={`${analysisSettingsOpen ? 'flex' : 'hidden'} flex-col gap-2 max-h-[55vh] overflow-y-auto border-t border-slate-100 pt-2`}
        >
          {/* 写真サイズ (カード表示時) */}
          {viewMode === 'card' && (
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <span className="shrink-0">写真サイズ</span>
              <input
                type="range"
                min={40}
                max={140}
                step={4}
                value={cardSize}
                onChange={(e) => setCardSize(Number(e.target.value))}
                className="w-28 cursor-pointer"
                aria-label="カードサイズ"
              />
              <span className="tabular-nums w-8 text-right">{cardSize}px</span>
            </label>
          )}

          {/* 表示形式 + 並替 + データソース */}
          <div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-600">表示:</span>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`text-xs px-2.5 py-1 rounded min-h-touch ${
                  viewMode === 'table'
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                テーブル
              </button>
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`text-xs px-2.5 py-1 rounded min-h-touch ${
                  viewMode === 'card'
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                カード
              </button>
            </div>
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <span>並替:</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="border border-slate-300 rounded px-2 py-1 text-xs min-h-touch"
                aria-label="並び替え"
              >
                {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                  <option key={k} value={k}>
                    {SORT_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            {hasClassifications && (
              <div className="flex items-center gap-2" role="radiogroup" aria-label="データソース">
                <span className="text-xs font-semibold text-slate-600">データソース</span>
                <button
                  type="button"
                  role="radio"
                  aria-checked={dataSource === 'post'}
                  onClick={() => switchDataSource('post')}
                  className={`text-xs px-3 py-1 rounded-full font-semibold min-h-touch ${
                    dataSource === 'post'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-slate-600 border border-slate-300'
                  }`}
                >
                  投稿単位
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={dataSource === 'cls'}
                  onClick={() => switchDataSource('cls')}
                  className={`text-xs px-3 py-1 rounded-full font-semibold min-h-touch ${
                    dataSource === 'cls'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-slate-600 border border-slate-300'
                  }`}
                >
                  AI認識物 ({classificationRows.length})
                </button>
              </div>
            )}
          </div>

          {/* 軸の設定 (カード表示時のみ意味を持つ) */}
          {viewMode === 'card' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 shrink-0">軸</span>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <span className="shrink-0">縦</span>
                <select
                  value={effVAxis}
                  onChange={(e) => setVAxisKey(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1 text-xs min-h-touch max-w-[40vw] sm:max-w-none"
                  aria-label="縦軸"
                >
                  {axisOptions.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  const v = effVAxis;
                  setVAxisKey(effHAxis);
                  setHAxisKey(v);
                }}
                aria-label="縦軸と横軸を入れ替え"
                className="text-primary-600 hover:text-primary-700 underline bg-transparent border-none cursor-pointer min-h-touch min-w-touch text-xs"
              >
                ⇄ 入替
              </button>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <span className="shrink-0">横</span>
                <select
                  value={effHAxis}
                  onChange={(e) => setHAxisKey(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1 text-xs min-h-touch max-w-[40vw] sm:max-w-none"
                  aria-label="横軸"
                >
                  {axisOptions.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {/* フィルタ (軸駆動 bucket chip) — 独立の折りたたみは廃止し、分析設定内に直接展開 */}
          <div className="flex flex-col gap-2 border-t border-slate-100 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600">フィルタ</span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                >
                  フィルタ解除 ({activeFilterCount})
                </button>
              )}
            </div>
            {axisOptions.length === 0 && (
              <p className="text-xs text-slate-400">絞込に使える軸がありません。</p>
            )}
            {axisOptions.map((opt) => {
              const groups = filterGroupsByKey.get(opt.key) ?? [];
              if (groups.length === 0) return null;
              const sel = filterSelections[opt.key] ?? new Set<string>();
              return (
                <div key={opt.key} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-600 shrink-0 min-w-[5rem]">
                    {opt.label}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {groups.map((g) => {
                      const checked = sel.has(g.code);
                      return (
                        <button
                          key={g.code}
                          type="button"
                          onClick={() => toggleFilterValue(opt.key, g.code)}
                          aria-pressed={checked}
                          className="px-2 py-0.5 rounded-full text-[11px] min-h-touch border"
                          style={
                            checked
                              ? {
                                  backgroundColor: g.color,
                                  color: contrastTextColor(g.color),
                                  borderColor: g.color,
                                }
                              : {
                                  backgroundColor: '#f1f5f9',
                                  color: '#334155',
                                  borderColor: '#e2e8f0',
                                }
                          }
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* キーワード検索 (最下段) */}
          <div className="border-t border-slate-100 pt-2">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="キーワード検索 (タイトル / 本文)"
              className="w-full text-sm px-3 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>
      </div>

      {/* ===== 本体 =====
          `min-h-0` 必須: flex item の既定 `min-height:auto` のままだと、中身 (カード/表) の高さ分
          だけこの div が伸びて親の h-full を超え、最下部の CTA が画面外へ押し出される。 */}
      <div className="flex-1 min-h-0 overflow-auto">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-12">
            条件に一致するデータがありません
          </p>
        ) : viewMode === 'card' ? (
          <CardGrid
            cells={gridCells}
            vGroups={vGroups}
            hGroups={hGroups}
            vAxisKey={effVAxis}
            hAxisKey={effHAxis}
            axisLabelOf={axisLabelOf}
            cardSize={cardSize}
            selectedSet={selectedSet}
            multiSelectEnabled={multiSelectEnabled}
            onToggleSelect={togglePostSelection}
            onSelectPost={onSelectPost}
          />
        ) : (
          <TableView
            posts={filtered}
            axisOptions={axisOptions}
            filterGroupsByKey={filterGroupsByKey}
            axisDeps={axisDeps}
            selectedSet={selectedSet}
            multiSelectEnabled={multiSelectEnabled}
            onToggleSelect={togglePostSelection}
            onSelectPost={onSelectPost}
          />
        )}
      </div>

      {/* ===== 最下部に固定の気づき作成 CTA (唯一の作成導線、コンテンツより常に手前) ===== */}
      {onProceedToInsight && filtered.length > 0 && (
        <div className="px-3 py-2 sm:px-4 sm:py-3 border-t border-slate-200 bg-slate-50 text-center shrink-0">
          <button
            type="button"
            onClick={handleProceed}
            className="w-full sm:w-auto min-h-touch px-6 sm:px-8 py-2.5 sm:py-2 rounded text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800"
          >
            💡 気づきの記入へ ({proceedCount}件)
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * テーブル表示 (動的軸カラム)
 * ========================================================================== */

function TableView({
  posts,
  axisOptions,
  filterGroupsByKey,
  axisDeps,
  selectedSet,
  multiSelectEnabled,
  onToggleSelect,
  onSelectPost,
}: {
  posts: PostMinimal[];
  axisOptions: { key: AxisKey; label: string }[];
  filterGroupsByKey: Map<string, AxisGroup[]>;
  axisDeps: Parameters<typeof postCodesForAxis>[3];
  selectedSet: Set<string>;
  multiSelectEnabled: boolean;
  onToggleSelect: (id: string) => void;
  onSelectPost: (post: PostMinimal) => void;
}) {
  // 軸カラムは地区のマスタ / 投稿設定の数だけ増える (逗子テストで 15 列超)。`w-full` だと
  // その全部をコンテナ幅に押し込むため、写真とタイトルが潰れて読めなくなっていた。
  // `w-max min-w-full` で各列を内容なりの幅にし、あふれる分は親 (overflow-auto) の
  // 横スクロールに逃がす。代わりに写真 / タイトルは左端に固定して、横スクロール中も
  // 「どの投稿の行か」を見失わないようにする。
  // 左固定列の開始位置 = チェックボックス w-10 (40px) + 写真 w-20 (80px)。
  const stickyPhotoLeft = multiSelectEnabled ? 'left-10' : 'left-0';
  const stickyTitleLeft = multiSelectEnabled ? 'left-[120px]' : 'left-20';
  return (
    <table className="w-max min-w-full text-xs border-collapse">
      <thead className="sticky top-0 bg-slate-50 z-20">
        <tr className="text-left text-slate-500">
          {multiSelectEnabled && <th className="sticky left-0 z-20 w-10 px-2 py-2 bg-slate-50" />}
          <th className={`sticky ${stickyPhotoLeft} z-20 w-20 px-2 py-2 bg-slate-50`}>写真</th>
          <th
            className={`sticky ${stickyTitleLeft} z-20 min-w-[200px] px-2 py-2 bg-slate-50 border-r border-slate-200`}
          >
            タイトル
          </th>
          {axisOptions.map((a) => (
            <th key={a.key} className="px-2 py-2 whitespace-nowrap">
              {a.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {posts.map((p) => (
          <tr key={p.id} className="group hover:bg-slate-50">
            {multiSelectEnabled && (
              <td className="sticky left-0 z-10 w-10 px-2 py-1.5 align-top bg-white group-hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selectedSet.has(p.id)}
                  onChange={() => onToggleSelect(p.id)}
                  aria-label={`${p.title} を選択`}
                  className="w-4 h-4"
                />
              </td>
            )}
            <td
              className={`sticky ${stickyPhotoLeft} z-10 w-20 px-2 py-1.5 align-top bg-white group-hover:bg-slate-50`}
            >
              <button
                type="button"
                onClick={() => onSelectPost(p)}
                aria-label={`${p.title} の詳細`}
                className="block rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-primary-400"
              >
                <Thumb post={p} size={56} />
              </button>
            </td>
            <td
              className={`sticky ${stickyTitleLeft} z-10 min-w-[200px] px-2 py-1.5 align-top bg-white group-hover:bg-slate-50 border-r border-slate-200`}
            >
              <button
                type="button"
                onClick={() => onSelectPost(p)}
                className="block w-full max-w-[260px] text-left hover:text-primary-700"
              >
                <span className="block font-semibold text-slate-800 line-clamp-2 break-words leading-snug">
                  {p.title}
                </span>
                <span className="block text-[10px] text-slate-400 truncate">
                  {p.authorNickname}
                </span>
              </button>
            </td>
            {axisOptions.map((a) => (
              <td key={a.key} className="px-2 py-1.5 align-top">
                <AxisCell
                  post={p}
                  axisKey={a.key}
                  groups={filterGroupsByKey.get(a.key) ?? []}
                  axisDeps={axisDeps}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Set の先頭要素だけを残す (Set は挿入順を保つ)。cls (AI 認識物単位) では絞らない。 */
function firstCodeOnly(codes: Set<string>, isClsMode: boolean): Set<string> {
  if (isClsMode || codes.size <= 1) return codes;
  for (const c of codes) return new Set([c]);
  return codes;
}

/** テーブルセル: post の指定軸 bucket を色付き chip で表示。 */
function AxisCell({
  post,
  axisKey,
  groups,
  axisDeps,
}: {
  post: PostMinimal;
  axisKey: AxisKey;
  groups: AxisGroup[];
  axisDeps: Parameters<typeof postCodesForAxis>[3];
}) {
  const codes = postCodesForAxis(post, axisKey, groups, axisDeps);
  const chips = [...codes]
    .map((code) => groups.find((g) => g.code === code) ?? null)
    .filter(Boolean) as AxisGroup[];
  if (chips.length === 0) return <span className="text-slate-300">—</span>;
  return (
    // 最大幅を内側の div で持つ (td の max-width は table-layout:auto では効かない)。
    // チップが多い軸でも 1 列が伸び続けず、行内で折り返す。
    <div className="flex flex-wrap gap-0.5 max-w-[220px]">
      {chips.map((g) => (
        <span
          key={g.code}
          className="px-1.5 py-0.5 rounded-full text-[9px]"
          style={{ backgroundColor: g.color, color: contrastTextColor(g.color) }}
        >
          {g.label}
        </span>
      ))}
    </div>
  );
}

/* ============================================================================
 * カード表示 (2 軸グルーピンググリッド、マトリクスと同じバケット)
 * ========================================================================== */

function CardGrid({
  cells,
  vGroups,
  hGroups,
  vAxisKey,
  hAxisKey,
  axisLabelOf,
  cardSize,
  selectedSet,
  multiSelectEnabled,
  onToggleSelect,
  onSelectPost,
}: {
  cells: Map<string, PostMinimal[]>;
  vGroups: AxisGroup[];
  hGroups: AxisGroup[];
  vAxisKey: AxisKey;
  hAxisKey: AxisKey;
  axisLabelOf: (k: AxisKey) => string;
  cardSize: number;
  selectedSet: Set<string>;
  multiSelectEnabled: boolean;
  onToggleSelect: (id: string) => void;
  onSelectPost: (post: PostMinimal) => void;
}) {
  // 縦軸の項目名 (1 列目) はスマホで幅を取り過ぎないよう上限を設け、長い名前は折り返す
  // (whitespace-normal + break-words)。軸セレクタは「分析設定」折りたたみへ移設済。
  // 分析画面はマトリクス/地図で選んだ投稿を見る場所なので、投稿が 1 件も無い行・列は出さない
  // (マスタ全項目や「空の項目も表示」設定の行列で画面が埋まり、肝心のカードが見えなくなるため)。
  const rowHasPosts = (v: AxisGroup) => hGroups.some((h) => cells.has(`${v.code}:${h.code}`));
  const colHasPosts = (h: AxisGroup) => vGroups.some((v) => cells.has(`${v.code}:${h.code}`));
  const shownVGroups = vGroups.filter(rowHasPosts);
  const shownHGroups = hGroups.filter(colHasPosts);
  return (
    <div className="p-2 sm:p-3 overflow-auto">
      {shownVGroups.length === 0 || shownHGroups.length === 0 ? (
        <p className="text-center text-sm text-slate-500 py-12">
          選んだ軸（{axisLabelOf(vAxisKey)} × {axisLabelOf(hAxisKey)}
          ）に該当するデータがありません。
        </p>
      ) : (
        <div className="inline-block min-w-full">
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-sky-50 border border-slate-200 px-1 sm:px-2 py-2 text-[10px] sm:text-[11px] font-bold text-slate-600 text-center z-10 max-w-[28vw] sm:max-w-none break-words">
                  {axisLabelOf(vAxisKey)} \ {axisLabelOf(hAxisKey)}
                </th>
                {shownHGroups.map((h) => (
                  <th
                    key={h.code}
                    className="bg-sky-50 border border-slate-200 px-2 py-2 text-[11px] font-bold text-center min-w-[88px] sm:min-w-[100px]"
                  >
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px]"
                      style={{ backgroundColor: h.color, color: contrastTextColor(h.color) }}
                    >
                      {h.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownVGroups.map((v) => (
                <tr key={v.code}>
                  <th className="sticky left-0 bg-sky-50/60 border border-slate-200 px-1 sm:px-2 py-2 text-[11px] font-bold text-center align-top z-10 max-w-[28vw] sm:max-w-none">
                    <span
                      className="inline-flex items-center gap-1.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] break-words whitespace-normal text-left leading-tight"
                      style={{ backgroundColor: v.color, color: contrastTextColor(v.color) }}
                    >
                      {v.label}
                    </span>
                  </th>
                  {shownHGroups.map((h) => {
                    const cell = cells.get(`${v.code}:${h.code}`) ?? [];
                    return (
                      <td
                        key={h.code}
                        className={`border border-slate-100 p-1 align-top ${
                          cell.length > 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        {cell.length > 0 && (
                          <div className="text-[10px] text-slate-400 mb-1">{cell.length}件</div>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {cell.map((p) => (
                            <GridCard
                              key={p.id}
                              post={p}
                              size={cardSize}
                              selected={selectedSet.has(p.id)}
                              multiSelectEnabled={multiSelectEnabled}
                              onToggleSelect={() => onToggleSelect(p.id)}
                              onSelectPost={() => onSelectPost(p)}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** グリッドセル内の写真ミニカード。 */
function GridCard({
  post,
  size,
  selected,
  multiSelectEnabled,
  onToggleSelect,
  onSelectPost,
}: {
  post: PostMinimal;
  size: number;
  selected: boolean;
  multiSelectEnabled: boolean;
  onToggleSelect: () => void;
  onSelectPost: () => void;
}) {
  return (
    <div className="relative" style={{ width: size }}>
      {multiSelectEnabled && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`${post.title} を選択`}
          className="absolute top-0.5 left-0.5 z-10 w-3.5 h-3.5 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <button
        type="button"
        onClick={onSelectPost}
        title={post.title}
        className={`block w-full rounded overflow-hidden border bg-white text-left hover:ring-2 hover:ring-primary-400 ${
          selected ? 'border-primary-500 ring-2 ring-primary-400' : 'border-slate-200'
        }`}
      >
        <Thumb post={post} size={Math.round(size * 0.7)} square />
        <div className="px-1 py-0.5 text-[9px] text-slate-700 line-clamp-1">{post.title}</div>
      </button>
    </div>
  );
}

/* ============================================================================
 * 写真サムネ (usePhotoUrl、写真無しは 📷)。
 * ========================================================================== */

function Thumb({ post, size, square }: { post: PostMinimal; size: number; square?: boolean }) {
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl ?? post.photoUrl);
  const style: React.CSSProperties = square
    ? { width: '100%', height: `${size}px` }
    : { width: `${size}px`, height: `${size}px` };
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className="object-cover w-full"
        style={style}
      />
    );
  }
  return (
    <div
      className={`flex items-center justify-center bg-slate-100 text-slate-400 ${
        photo.status === 'loading' ? 'animate-pulse' : ''
      }`}
      style={style}
    >
      <span style={{ fontSize: `${Math.max(12, Math.round(size * 0.4))}px` }}>📷</span>
    </div>
  );
}
